import { apiFetch } from "./client";
import type { Page } from "./admin";

/**
 * The register of settled intakes, read from the ERP's invoices.
 *
 * Separate from `supplierSettlements`, which is the portal's own settlement note: one is an amount
 * read off an invoice, the other an amount the portal computed, and a screen that mixed them could
 * not say which it was showing.
 */
export type ErpSettlementRow = {
  deliveryId: string;
  deliveryNumber: string | null;
  settledOn: string | null;
  companyId: string;
  companyName: string | null;
  netKg: number | null;
  invoiceId: string;
  invoiceNumber: string | null;
  issueDate: string | null;
  currency: string | null;
  documentType: string | null;
  productName: string | null;
  unitPrice: number | null;
  /** The whole invoice's amount — spread over `coveredReceipts` receipts. */
  invoiceAmount: number | null;
  coveredReceipts: number;
  /** How many resolved to an intake; fewer than `coveredReceipts` means no amount is attributed. */
  resolvedReceipts: number;
  lineCount: number;
  /** This intake's share, by weighed mass. Empty when the invoice has several articles, or when it
   * names a receipt the portal does not hold — in either case the attribution would be a guess. */
  amountForThisDelivery: number | null;
};

export type ErpSettlementFilters = {
  companyId?: string;
  from?: string;
  to?: string;
  currency?: string;
  q?: string;
};

function upit(f: ErpSettlementFilters, page: number, size: number): string {
  const p = new URLSearchParams();
  if (f.companyId) p.set("companyId", f.companyId);
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.currency) p.set("currency", f.currency);
  if (f.q) p.set("q", f.q);
  p.set("page", String(page));
  p.set("size", String(size));
  return p.toString();
}

export const erpSettlementsApi = {
  list: (f: ErpSettlementFilters, page: number, size: number) =>
    apiFetch<Page<ErpSettlementRow>>(`/erp-settlements?${upit(f, page, size)}`),
  /** Intakes the ERP settled whose invoice is not resolved — what the register does NOT show. */
  unresolved: () => apiFetch<{ settledWithoutInvoice: number }>(`/erp-settlements/unresolved`),
  currencies: (companyId?: string) =>
    apiFetch<string[]>(`/erp-settlements/currencies${companyId ? `?companyId=${companyId}` : ""}`),
};
